import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  loginCustomer,
  saveCustomerSession,
  getCurrentCustomer
} from '@/lib/shopifyCustomer';
import { useAuthStore } from '@/store';

const SHOPIFY_ERROR_MESSAGES = {
  UNIDENTIFIED_CUSTOMER: 'E-mail ou senha incorretos.',
  CUSTOMER_DISABLED: 'Esta conta está desativada. Entre em contato com o suporte.',
  TOO_SHORT: 'Senha muito curta.',
  BLANK: 'Preencha todos os campos.',
};

function translateLoginError(error) {
  return SHOPIFY_ERROR_MESSAGES[error.code] || 'E-mail ou senha incorretos.';
}

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    senha: ''
  });
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setCustomer } = useAuthStore();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError('');

    try {
      const result = await loginCustomer(formData);

      if (result.customerUserErrors?.length > 0) {
        setFormError(translateLoginError(result.customerUserErrors[0]));
        return;
      }

      if (!result.customerAccessToken) {
        setFormError('Não foi possível fazer login. Tente novamente.');
        return;
      }

      saveCustomerSession(result.customerAccessToken);
      const currentCustomer = await getCurrentCustomer(result.customerAccessToken.accessToken);
      if (currentCustomer) setCustomer(currentCustomer);

      toast.success('Login realizado com sucesso!');

      const rawRedirect = searchParams.get('redirect') || '/';
      const redirect = rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : '/';
      navigate(redirect, { replace: true });
    } catch {
      setFormError('Erro ao conectar. Verifique sua conexão e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 flex items-center">
      <div className="max-w-[1400px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto bg-white rounded-2xl shadow-sm p-10"
        >
          <h1 className="font-heading text-4xl font-bold text-center mb-8">
            Entrar
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block mb-2">E-mail</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full h-12 rounded-xl border px-4 focus:ring-2 focus:ring-accent/30 outline-none"
                required
              />
            </div>

            <div>
              <label className="block mb-2">Senha</label>
              <input
                type="password"
                name="senha"
                value={formData.senha}
                onChange={handleChange}
                className="w-full h-12 rounded-xl border px-4 focus:ring-2 focus:ring-accent/30 outline-none"
                required
              />
            </div>

            {formError && (
              <p className="text-sm text-red-600 text-center -mt-2">{formError}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-accent text-white rounded-full h-12"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>

            <p className="text-center text-sm text-gray-500">
              Ainda não tem conta?{' '}
              <Link to="/register" className="text-accent font-semibold">
                Criar conta
              </Link>
            </p>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
